import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { X, Printer, Download, FileImage, ExternalLink, AlertCircle } from 'lucide-react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import LoadingSpinner from '../LoadingSpinner'
import { exportReceiptPDF, exportReceiptPNG } from '../../utils/pdfExport'
import { useAuthStore } from '../../store/authStore'
import ReceiptDocument from './ReceiptDocument'

export default function ReceiptModal({ paymentId, isOpen, onClose }) {
  const { user } = useAuthStore()
  const receiptRef = useRef(null)
  const [receipt, setReceipt] = useState(null)
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(null) // 'pdf' | 'png' | null
  const [error, setError] = useState(null)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)')
    setIsMobile(mql.matches)
    const onChange = (e) => setIsMobile(e.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    if (isOpen && paymentId) {
      fetchReceipt()
    } else {
      setReceipt(null)
      setError(null)
    }
  }, [isOpen, paymentId])

  const fetchReceipt = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get(`/finance/receipts/${paymentId}`)
      setReceipt(data.receipt)
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to load receipt'
      setError(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadPDF = async () => {
    if (!receipt) return
    setDownloading('pdf')
    try {
      await exportReceiptPDF(receipt, { element: receiptRef.current })
      toast.success('Receipt PDF downloaded')
    } catch (err) {
      console.error('Receipt PDF download error:', err)
      toast.error('Failed to generate receipt PDF')
    } finally {
      setDownloading(null)
    }
  }

  const handleDownloadPNG = async () => {
    if (!receipt) return
    setDownloading('png')
    try {
      await exportReceiptPNG(receipt, { element: receiptRef.current })
      toast.success('Receipt PNG downloaded')
    } catch (err) {
      console.error('Receipt PNG download error:', err)
      toast.error('Failed to generate receipt PNG')
    } finally {
      setDownloading(null)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  if (!isOpen) return null

  const fullPageUrl =
    user?.user_type === 'admin'
      ? `/admin/receipts/${paymentId}`
      : user?.user_type === 'finance'
        ? `/finance/receipts/${paymentId}`
        : `/student/receipts/${paymentId}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-6 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-5xl max-h-[95vh] flex flex-col bg-cinema-charcoal border border-white/10 shadow-2xl rounded-sm overflow-hidden animate-fadeIn">
        {/* Modal Top Toolbar — not part of the captured .receipt-document */}
        <div className="px-3 sm:px-4 py-2 sm:py-3 bg-cinema-black border-b border-white/10 flex items-center justify-between gap-2 no-print">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-2 h-2 rounded-full bg-cinema-gold flex-shrink-0" />
            <span className="font-display text-xs sm:text-sm font-medium text-white truncate">
              Receipt {receipt?.receipt_number ? `· ${receipt.receipt_number}` : ''}
            </span>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {receipt && (
              <>
                <button
                  onClick={handleDownloadPDF}
                  disabled={!!downloading}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 text-xs font-sans font-medium text-cinema-soft hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-sm transition-colors disabled:opacity-50"
                  title="Download PDF"
                >
                  <Download size={12} className={downloading === 'pdf' ? 'animate-bounce' : ''} />
                  <span className="hidden sm:inline">PDF</span>
                </button>
                <button
                  onClick={handleDownloadPNG}
                  disabled={!!downloading}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 text-xs font-sans font-medium text-cinema-soft hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-sm transition-colors disabled:opacity-50"
                  title="Download PNG image"
                >
                  <FileImage size={12} className={downloading === 'png' ? 'animate-bounce' : ''} />
                  <span className="hidden sm:inline">PNG</span>
                </button>
                <button
                  onClick={handlePrint}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 text-xs font-sans font-medium text-cinema-gold bg-cinema-gold/10 hover:bg-cinema-gold/20 border border-cinema-gold/30 rounded-sm transition-colors"
                  title="Print / Save as PDF"
                >
                  <Printer size={12} />
                  <span className="hidden sm:inline">Print</span>
                </button>
                <Link
                  to={fullPageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="p-1 text-cinema-muted hover:text-white transition-colors"
                  title="Open full page in new tab"
                >
                  <ExternalLink size={14} />
                </Link>
              </>
            )}
            <button
              onClick={onClose}
              className="p-1 text-cinema-muted hover:text-white transition-colors"
              title="Close modal"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Modal Body / Scrollable Content */}
        <div className="flex-1 overflow-auto p-2 sm:p-6 bg-cinema-black/40">
          {loading ? (
            <div className="py-16">
              <LoadingSpinner label="Loading receipt details..." />
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <AlertCircle size={36} className="mx-auto mb-3 text-red-400" />
              <p className="text-sm font-medium text-white">{error}</p>
              <p className="text-xs text-cinema-muted mt-1">
                The payment ID may be invalid or not recorded in the database.
              </p>
              <button
                onClick={fetchReceipt}
                className="mt-4 px-4 py-2 text-xs text-cinema-gold bg-cinema-gold/10 border border-cinema-gold/30 rounded-sm"
              >
                Retry
              </button>
            </div>
          ) : receipt ? (
            <ReceiptDocument ref={receiptRef} receipt={receipt} isMobile={isMobile} />
          ) : null}
        </div>
      </div>
    </div>
  )
}
